import { SendVisitConfirmationButton } from '@/components/organisms/jobs/SendVisitConfirmationButton';
import { VisitCustomerConfirmationActions } from '@/components/organisms/jobs/VisitCustomerConfirmationActions';
import { Fragment, useRef, useState, type ElementType, type ReactNode } from 'react';
import {
  createFileRoute,
  getRouteApi,
  Link,
  Outlet,
  useChildMatches,
  useRouter,
} from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  Briefcase,
  ClipboardCheck,
  Clock,
  CreditCard,
  FilePlus2,
  FileText,
  History,
  Layers,
  ListChecks,
  Loader2,
  Lock,
  MessageSquare,
  MoreVertical,
  Paperclip,
  Plus,
  Receipt,
  Star,
  StickyNote,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  ArrivalWindowSection,
  VisitScheduleSection,
  ConfirmationSection,
  CustomerNotesSection,
  InternalNotesSection,
  IssueSummarySection,
  VisitRemindersSection,
} from '@/components/organisms/jobs/AppointmentDetailsPanel';
import { AppointmentChecklists } from '@/components/organisms/jobs/AppointmentChecklists';
import { AppointmentTimeEntries } from '@/components/organisms/jobs/AppointmentTimeEntries';
import { AppointmentFormSubmissions } from '@/components/organisms/jobs/AppointmentFormSubmissions';
import { EntityNotes } from '@/components/organisms/notes/EntityNotes';
import { AppointmentAuthSignoffCard } from '@/components/organisms/jobs/JobAuthSignoffCard';
import { ExpenseDetailDrawer } from '@/components/organisms/expenses/ExpenseDetailDrawer';
import { NewExpenseDrawer } from '@/components/organisms/expenses/NewExpenseDrawer';
import { useExpenses } from '@/hooks/expenses/useExpenses';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { VisitAside } from '@/components/organisms/jobs/VisitAside';
import { VisitCommandBar } from '@/components/organisms/jobs/VisitCommandBar';
import { VisitOverviewStrip } from '@/components/organisms/jobs/VisitOverviewStrip';
import { VisitReviewControls } from '@/components/organisms/jobs/VisitReviewControls';
import {
  VisitDecisionDialog,
  useVisitNextStep,
  type VisitNextStep,
} from '@/components/organisms/jobs/VisitNextStepCard';
import {
  continueStartAfterAuthorization,
  openStartAuthorization,
  setAuthorizationDialogOpen,
} from '@/components/organisms/jobs/support/start-authorization';
import { SeriesActionsCard } from '@/components/organisms/jobs/SeriesActionsCard';
import { VisitSchedulingPanel } from '@/components/organisms/jobs/visits/VisitSchedulingPanel';
import { RestoreVisitButton } from '@/components/organisms/jobs/visits/RestoreVisitButton';
import {
  VisitAddChargeSheet,
  VisitExtraCharges,
} from '@/components/organisms/jobs/visits/VisitCharges';
import { getActions, type ActionDef } from '@/components/organisms/jobs/visits/visit-actions';
import { RescheduleAppointmentDialog } from '@/components/organisms/jobs/visits/RescheduleAppointmentDialog';
import type { CrewMember } from '@/components/organisms/scheduler/crewAssignmentModel';
import {
  useAppointmentChecklists,
  useChecklistOfflineWorkflow,
} from '@/hooks/serviceRequests/useChecklistInstances';
import { useApiQuery } from '@/hooks/useApiQuery';
import { cn } from '@/lib/utils';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { normalizeJob } from '@/lib/normalizeJob';
import { jobKeys } from '@/lib/queryKeys';
import { useContact } from '@/hooks/contacts/useContact';
import { useJobAppointment } from '@/hooks/jobs/useJobAppointments';
import { useJobReviews } from '@/hooks/jobs/useJobReviews';
import { useSkipJobAppointment, useCancelJobAppointment } from '@/hooks/jobs/useJobAppointments';
import { useAppointmentReminders } from '@/hooks/reminders/useAppointmentReminders';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import { JobInvoicesSection } from '@/components/organisms/jobs/JobInvoicesSection';
import { NewInvoiceDrawer } from '@/components/organisms/invoices/NewInvoiceDrawer';
import { RecurringActivityTab } from '@/components/organisms/jobs/recurring/RecurringActivityTab';
import { JobRelationshipsCard } from '@/components/organisms/jobs/JobRelationshipsCard';
import {
  canCreateEstimateFromJob,
  estimateFromJobSearch,
  proposalFromJobSearch,
} from '@/lib/jobs/serviceCall';
import type { JobAppointment, JobAppointmentStatus } from '@/types/jobAppointment';
import type { Job } from '@/types/job';
import type { Expense } from '@/types/settings';

type VisitDetailTab = 'work' | 'billing' | 'notes' | 'comms' | 'history';

const jobsListRouteApi = getRouteApi('/_dashboard/dashboard/jobs');

function normalizeVisitTab(value: unknown): VisitDetailTab | undefined {
  return value === 'billing' ||
    value === 'notes' ||
    value === 'comms' ||
    value === 'work' ||
    value === 'history'
    ? value
    : undefined;
}

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/appointments/$appointmentId')({
  validateSearch: (search: Record<string, unknown>): { visitTab?: VisitDetailTab } => ({
    // Accept the legacy `tab` key so old Billing/Notes links still land in the
    // expected Visit section, but emit a child-specific key. The parent Job
    // route owns `tab`; sharing it made the two route validators intersect.
    visitTab: normalizeVisitTab(search.visitTab) ?? normalizeVisitTab(search.tab),
  }),
  component: AppointmentDetailPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const READ_ONLY_JOB_STATUSES = new Set(['CANCELLED', 'CLOSED', 'ARCHIVED']);

function isAppointmentLocked(appointment: JobAppointment): boolean {
  return (
    appointment.status === 'COMPLETED' ||
    appointment.status === 'CANCELLED' ||
    appointment.status === 'CANCELLATION_PENDING' ||
    appointment.status === 'EXPIRED' ||
    appointment.status === 'SKIPPED'
  );
}

function lockedReasonFor(status: JobAppointmentStatus): 'completed' | 'cancelled' | 'readonly' {
  if (status === 'COMPLETED') return 'completed';
  if (
    status === 'CANCELLED' ||
    status === 'CANCELLATION_PENDING' ||
    status === 'EXPIRED' ||
    status === 'SKIPPED'
  ) {
    return 'cancelled';
  }
  return 'readonly';
}

// ─── Action menu ─────────────────────────────────────────────────────────────

function AppointmentActionMenu({
  jobId,
  appointment,
  isRecurring,
  onReschedule,
  onReassign,
  jobUnavailableHint,
  skip,
  cancel,
  busy = false,
}: {
  jobId: string;
  appointment: JobAppointment;
  isRecurring: boolean;
  onReschedule: () => void;
  onReassign: () => void;
  /** Why Reschedule / Reassign can't run yet (the job hasn't loaded). */
  jobUnavailableHint?: string | null;
  skip: ReturnType<typeof useSkipJobAppointment>;
  cancel: ReturnType<typeof useCancelJobAppointment>;
  /** Disable the whole menu while the header CTA's transition is in flight, so a
   *  skip/cancel can't race an advance/complete (and vice-versa). */
  busy?: boolean;
}) {
  const router = useRouter();
  const isPending = skip.isPending || cancel.isPending || busy;
  const actions = getActions(appointment, isRecurring, jobUnavailableHint);

  if (actions.length === 0) return null;

  function run(action: ActionDef) {
    if (action.disabledHint) return;
    const apptId = appointment.id;
    if (action.kind === 'reschedule') {
      onReschedule();
      return;
    }

    if (action.kind === 'reassign') {
      onReassign();
      return;
    }

    if (action.kind === 'createNew') {
      void router.navigate({
        to: '/dashboard/jobs/$id/appointments/new',
        params: { id: jobId },
      });
      return;
    }

    if (action.kind === 'skip') {
      skip.mutate({ appointmentId: apptId, reason: 'Skipped from visit detail menu' });
      return;
    }

    if (action.kind === 'cancel') {
      cancel.mutate(
        { appointmentId: apptId },
        {
          onSuccess: () =>
            void router.navigate({ to: '/dashboard/jobs/$id', params: { id: jobId } }),
        },
      );
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon-sm" disabled={isPending} title="Actions">
          {isPending ? <Loader2 size={14} className="animate-spin" /> : <MoreVertical size={14} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {actions.map((action, idx) => {
          const key = action.kind;
          const isCancel = action.kind === 'cancel';
          const Icon = action.icon;
          return (
            <Fragment key={key}>
              {isCancel && idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className={isCancel ? 'text-destructive focus:text-destructive' : undefined}
                disabled={Boolean(action.disabledHint)}
                onClick={() => run(action)}
              >
                <Icon className="mr-2 size-3.5" />
                {action.disabledHint ? (
                  <span className="flex min-w-0 flex-col">
                    <span>{action.label}</span>
                    <span className="text-[10px] leading-tight text-muted-foreground">
                      {action.disabledHint}
                    </span>
                  </span>
                ) : (
                  action.label
                )}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Section chrome ─────────────────────────────────────────────────────────────

/** Card chrome for a cockpit section: icon + title + optional sub-label + action. */
function SectionCard({
  icon: Icon,
  title,
  sub,
  action,
  children,
  bodyClassName,
}: {
  icon: ElementType;
  title: string;
  sub?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <h3 className="flex min-w-0 items-center gap-2 text-sm font-semibold">
          <Icon className="size-4 shrink-0 text-brand-charcoal" />
          <span className="truncate">{title}</span>
          {sub != null ? (
            <span className="truncate text-xs font-medium text-brand-light-grey">{sub}</span>
          ) : null}
        </h3>
        {action != null ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </div>
      <div className={cn('px-4 pb-4 sm:px-5', bodyClassName)}>{children}</div>
    </section>
  );
}

// ─── Charges (mode-aware) ──────────────────────────────────────────────────────

/** Where a per-visit extra (additionalItem) actually bills, by billing mode. */
function extrasBillingHint(billingMode: string | undefined, isRecurring: boolean): string {
  if (billingMode === 'SUBSCRIPTION') return 'Extras fold into the subscription cycle invoice.';
  if (billingMode === 'PREPAID') return 'Extras bill as a separate add-on invoice on completion.';
  if (isRecurring) return "Extras bill on this visit's invoice when it completes.";
  return "Extras are added to the job's invoice when the job completes.";
}

function ChargesCard({
  appointment,
  job,
  locked,
}: {
  appointment: JobAppointment;
  job: Job | undefined;
  locked: boolean;
}) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const jobId = job?.id;
  const [addOpen, setAddOpen] = useState(false);

  const additional = appointment.additionalItems ?? [];
  const basePrice = Number(appointment.totals?.baseItemsTotal ?? 0);
  // Total savings on this visit vs list, folded into the visit total by the
  // backend (frozen base + member-priced additions + any line/manual discounts).
  // Shown as its own row so the gap between the list-priced lines above and the
  // discounted visit total below is explained. GENERIC label — the amount is the
  // full delta, not membership-only, so labeling it "Membership" would print a
  // promo/manual discount as fake membership savings.
  const visitDiscount = Number(appointment.totals?.appointmentDiscount ?? 0);
  const fallbackTotal =
    basePrice + additional.reduce((s, it) => s + Number(it.basePrice ?? 0) * (it.quantity ?? 1), 0);
  // Trust the backend's computed total whenever it's present — including a
  // legitimate $0 (a comped or fully-discounted visit). Only fall back to the
  // line-sum when the total is genuinely absent, so a missing total never shows
  // as blank while a real $0 is still honoured.
  const rawApptTotal = appointment.totals?.appointmentTotal;
  const computedTotal = rawApptTotal != null ? Number(rawApptTotal) : fallbackTotal;
  const billingMode = job?.recurringConfig?.billingMode;
  const jobLevel = billingMode === 'SUBSCRIPTION' || billingMode === 'PREPAID';
  const hasCharges = basePrice > 0 || additional.length > 0;
  const isRecurring = job?.bookingType === 'RECURRING';
  const canEdit = !locked && Boolean(jobId);
  const modeLabel =
    billingMode === 'SUBSCRIPTION'
      ? 'Subscription · job-level'
      : billingMode === 'PREPAID'
        ? 'Prepaid · job-level'
        : 'Per-visit · on completion';

  if (pricingHidden) return null;

  return (
    <SectionCard
      icon={CreditCard}
      title="Charges"
      action={
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[11px] font-medium">
            {modeLabel}
          </Badge>
          {canEdit ? (
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="size-3.5" />
              Add item
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="text-sm">
        {/* Base — job-level modes bill the base elsewhere; per-visit shows the line. */}
        {jobLevel ? (
          <p className="pb-2 text-muted-foreground">
            Base service billed at the job level.{' '}
            {jobId ? (
              <Link
                to="/dashboard/jobs/$id"
                params={{ id: jobId }}
                search={{ tab: 'finances' }}
                className="font-medium text-info hover:underline"
              >
                View job billing →
              </Link>
            ) : null}
          </p>
        ) : basePrice > 0 ? (
          <div className="flex items-center justify-between gap-3 border-b border-dashed border-border py-2.5">
            <span>
              Base service
              {appointment.serviceType?.name ? ` — ${appointment.serviceType.name}` : ''}
            </span>
            <span className="font-mono font-semibold tabular-nums">
              {formatCurrency(basePrice)}
            </span>
          </div>
        ) : null}

        {/* Extras (per-visit add-ons) — with remove when editable. */}
        <VisitExtraCharges jobId={jobId ?? ''} appointment={appointment} canEdit={canEdit} />

        {/* Visit discount — the full list→net delta (membership + any promo/manual),
            per-visit only; folded into the total below. Generic label so a
            non-membership discount isn't mislabeled as membership savings. */}
        {!jobLevel && visitDiscount > 0 ? (
          <div className="flex items-center justify-between gap-3 py-2.5 text-success">
            <span>Discount</span>
            <span className="font-mono tabular-nums">−{formatCurrency(visitDiscount)}</span>
          </div>
        ) : null}

        {/* Total — per-visit only (job-level extras bill separately, no single total). */}
        {!jobLevel && hasCharges ? (
          <div className="mt-1 flex items-center justify-between gap-3 border-t-2 border-border pt-2.5 text-[15px] font-bold">
            <span>Visit total</span>
            <span className="font-mono tabular-nums">{formatCurrency(computedTotal)}</span>
          </div>
        ) : null}

        {/* Empty per-visit visit with nothing on it yet. */}
        {!jobLevel && !hasCharges ? (
          <p className="py-1 text-muted-foreground">No charges on this visit yet.</p>
        ) : null}
      </div>

      {/* Transparency: where the extra will bill. */}
      {additional.length > 0 || canEdit ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {extrasBillingHint(billingMode, isRecurring)}
        </p>
      ) : null}

      <VisitAddChargeSheet
        jobId={jobId ?? ''}
        appointmentId={appointment.id}
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </SectionCard>
  );
}

// ─── Expenses (compact table) ───────────────────────────────────────────────────

function ExpensesCard({
  expenses,
  locked,
  onAdd,
  onSelect,
}: {
  expenses: Expense[];
  locked: boolean;
  onAdd: () => void;
  onSelect: (id: string) => void;
}) {
  const total = expenses.reduce((s, e) => s + Number(e.total ?? 0), 0);
  return (
    <SectionCard
      icon={Receipt}
      title="Expenses"
      sub={
        expenses.length > 0
          ? `${expenses.length} ${expenses.length === 1 ? 'item' : 'items'}`
          : undefined
      }
      action={
        !locked ? (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="size-3.5" />
            Add expense
          </Button>
        ) : undefined
      }
    >
      {expenses.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3.5 py-3 text-sm text-muted-foreground">
          No materials or costs logged against this visit yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-[11px] uppercase tracking-wide text-brand-light-grey">
              <th className="pb-2 text-left font-semibold">Item</th>
              <th className="pb-2 text-left font-semibold">Type</th>
              <th className="pb-2 text-left font-semibold">Status</th>
              <th className="pb-2 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((exp) => (
              <tr
                key={exp.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/40"
                onClick={() => onSelect(exp.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(exp.id);
                  }
                }}
              >
                <td className="py-2.5 pr-2 font-medium">{exp.name}</td>
                <td className="py-2.5 pr-2">
                  <Badge variant="outline" className="text-[10px] font-medium capitalize">
                    {String(exp.expenseType).replace(/_/g, ' ').toLowerCase()}
                  </Badge>
                </td>
                <td className="py-2.5 pr-2">
                  <ExpenseStatusBadge status={exp.status} />
                </td>
                <td className="py-2.5 text-right font-mono tabular-nums">
                  {formatCurrency(Number(exp.total ?? 0))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border font-bold">
              <td className="pt-2.5" colSpan={3}>
                Total
              </td>
              <td className="pt-2.5 text-right font-mono tabular-nums">{formatCurrency(total)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </SectionCard>
  );
}

function ExpenseStatusBadge({ status }: { status: Expense['status'] }) {
  const tone: 'secondary' | 'outline' | 'destructive' =
    status === 'APPROVED' || status === 'REIMBURSED'
      ? 'secondary'
      : status === 'REJECTED' || status === 'VOIDED'
        ? 'destructive'
        : 'outline';
  return (
    <Badge variant={tone} className="text-[10px] font-medium capitalize">
      {status.toLowerCase()}
    </Badge>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/** The state-aware primary CTA, rendered in the header next to Maps + ⋯ (mockup). */
function HeaderPrimaryCta({ nextStep, busy = false }: { nextStep: VisitNextStep; busy?: boolean }) {
  const primary = nextStep.state?.primary;
  if (!primary) return null;
  const Icon = primary.icon;
  return (
    <Button
      size="sm"
      variant={primary.variant ?? 'default'}
      disabled={nextStep.isPending || busy}
      onClick={() => nextStep.runAction(primary.action)}
    >
      {nextStep.isPending ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Icon className="size-3.5" />
      )}
      {primary.label}
    </Button>
  );
}

/**
 * Keep job-level work available from the visit cockpit. ServiceTitan users are
 * accustomed to estimates, the invoice, and history living in the job they are
 * currently working; requiring a round trip through the parent job makes a
 * visit feel like a separate work order even though it is only an appointment.
 */
function JobWorkspaceMenu({
  job,
  appointmentId,
  pricingHidden,
  onCreateInvoice,
  onOpenHistory,
}: {
  job: Job;
  /** The visit this cockpit is on — an estimate raised here is quoted on it. */
  appointmentId: string;
  pricingHidden: boolean;
  onCreateInvoice: () => void;
  onOpenHistory: () => void;
}) {
  const router = useRouter();
  const jobsListSearch = jobsListRouteApi.useSearch();
  const canCreateEstimates = useHasPermission('estimates:create');
  const canCreateInvoices = useHasPermission('invoices:create');
  const status = String(job.statusPhase ?? job.status).toUpperCase();
  // A completed diagnostic visit may produce an estimate later. Only terminal
  // Job states close the sales loop; invoicing follows the same boundary.
  const estimateReadOnly = READ_ONLY_JOB_STATUSES.has(status);
  const invoiceReadOnly = READ_ONLY_JOB_STATUSES.has(status);
  const canCreateEstimate = canCreateEstimateFromJob(job, {
    canCreateEstimates,
    pricingHidden,
    readOnly: estimateReadOnly,
  });
  const canCreateInvoice = canCreateInvoices && !pricingHidden && !invoiceReadOnly;

  // Same entry as the job header's "Create estimate": the manual/template
  // picker, empty, with nothing persisted until the operator saves — except
  // that from a visit, the visit is named, so the presenter is exact.
  function handleCreateEstimate() {
    void router.navigate({
      to: '/dashboard/estimates/new',
      search: estimateFromJobSearch(job, { originAppointmentId: appointmentId }) as never,
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" title="Job workspace">
          <Briefcase className="size-3.5" />
          Job
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          onSelect={() =>
            void router.navigate({
              to: '/dashboard/jobs/$id',
              params: { id: job.id },
              search: { ...jobsListSearch, tab: 'overview' },
            })
          }
        >
          <Briefcase className="mr-2 size-3.5" />
          Open job overview
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onOpenHistory}>
          <History className="mr-2 size-3.5" />
          Job history
        </DropdownMenuItem>

        {canCreateEstimate || canCreateInvoice ? <DropdownMenuSeparator /> : null}

        {canCreateEstimate ? (
          <DropdownMenuItem onSelect={handleCreateEstimate}>
            <FilePlus2 className="mr-2 size-3.5" />
            Create estimate
          </DropdownMenuItem>
        ) : null}
        {canCreateEstimate ? (
          <DropdownMenuItem
            onSelect={() =>
              void router.navigate({
                to: '/dashboard/proposals/new',
                search: proposalFromJobSearch(job) as never,
              })
            }
          >
            <Layers className="mr-2 size-3.5" />
            Create proposal
          </DropdownMenuItem>
        ) : null}
        {canCreateInvoice ? (
          <DropdownMenuItem onSelect={onCreateInvoice}>
            <Receipt className="mr-2 size-3.5" />
            Create invoice
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AppointmentDetailPage() {
  const { id, appointmentId } = Route.useParams();
  const { visitTab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const jobsListSearch = jobsListRouteApi.useSearch();
  const childMatches = useChildMatches();
  const [reassignOpen, setReassignOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [collectAuthorizationOpen, setCollectAuthorizationOpen] = useState(false);
  const startAfterAuthorization = useRef(false);
  const [newExpenseOpen, setNewExpenseOpen] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [prefilledCrew, setPrefilledCrew] = useState<CrewMember[] | null>(null);
  const pricingHidden = useIsFieldHidden('PRICING');
  const canViewInvoices = useHasPermission('invoices:view');
  const canCreateInvoices = useHasPermission('invoices:create');

  const { data: appointment, isLoading, isError } = useJobAppointment(id, appointmentId);
  const {
    data: job,
    isPending: jobPending,
    isError: jobError,
  } = useApiQuery<Job>({
    queryKey: jobKeys.detail(id),
    queryFn: async () => normalizeJob(await apiGet(operationsClient, jobEndpoints.detail(id))),
    enabled: Boolean(id),
    showErrorToast: false,
  });
  // Every crew / reschedule dialog below is gated on `job`, so until it lands
  // their triggers open nothing at all. This query also swallows its error
  // toast, so a failed load is otherwise completely silent — a visit whose job
  // was deleted renders a live "Manage crew" button that does nothing when
  // clicked. Carry the reason to the controls instead.
  const jobUnavailableHint = job
    ? null
    : jobPending
      ? 'Loading job details…'
      : jobError
        ? "This visit's job could not be loaded"
        : 'Job details unavailable';
  const { data: contact } = useContact(job?.contactId ?? '', { enabled: Boolean(job?.contactId) });
  const isRecurring = job?.bookingType === 'RECURRING';
  const { data: jobReviews } = useJobReviews(id, isRecurring);
  const visitReview = jobReviews?.find((review) => review.appointmentId === appointmentId);
  // State-aware next-step engine shared with the stepper card; the header renders its
  // primary CTA (Dispatch / Start / Complete / …) so the stepper hides its own.
  const nextStep = useVisitNextStep(appointment, id, {
    // Only once the job has loaded: until then a recurring visit looks one-off.
    canMarkNoShow: Boolean(job) && !isRecurring,
    // BR-77 — answering a customer's reschedule request means moving the visit,
    // and the dialog that does it lives on this route.
    onReschedule: () => setRescheduleOpen(true),
    // BR-77 — a required visit authorization is a Start prerequisite, not a
    // terminal error. The next-step engine turns Start into "Authorize & start"
    // and opens the collection flow already rendered in Completion below.
    onCollectAuthorization: () =>
      openStartAuthorization({
        activeTab: visitTab ?? 'work',
        startIntent: startAfterAuthorization,
        showWorkTab: () => {
          void navigate({ search: (previous) => ({ ...previous, visitTab: 'work' }) });
        },
        openDialog: () => setCollectAuthorizationOpen(true),
      }),
  });
  // Skip/cancel live here (not inside the action menu) so their pending state is shared
  // with the header CTA — neither can fire a status transition while the other is in flight.
  const skipMutation = useSkipJobAppointment(id);
  const cancelMutation = useCancelJobAppointment(id);
  const menuBusy = skipMutation.isPending || cancelMutation.isPending;
  const checklistsQuery = useAppointmentChecklists(appointmentId);
  const checklistOffline = useChecklistOfflineWorkflow(appointmentId, checklistsQuery.data);
  const checklistInstances = checklistOffline.instances;
  const isVisitLocked = appointment ? isAppointmentLocked(appointment) : false;
  // Bumped by the card's "Add note" action — EntityNotes opens its composer on
  // each change, so the card header keeps the affordance.
  const [addNoteSignal, setAddNoteSignal] = useState(0);
  const blockingTotal = checklistInstances.filter(
    (instance) => instance.blocksCompletion === true,
  ).length;
  const blockingPending = checklistInstances.filter(
    (instance) => instance.blocksCompletion === true && instance.status !== 'COMPLETED',
  ).length;
  const appointmentReminders = useAppointmentReminders(appointmentId);
  const confirmationReminder = appointmentReminders.confirmation;
  const jobReminderReminder = appointmentReminders.reminder;
  const expensesQuery = useExpenses(job?.id, { appointmentId, limit: 50 });
  const visitExpenses = expensesQuery.data?.data ?? [];

  if (childMatches.length > 0) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 py-5">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (isError || !appointment) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-5 py-5">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Visit not found.
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void router.navigate({
              to: '/dashboard/jobs/$id',
              params: { id },
              search: { ...jobsListSearch, tab: 'overview' },
            })
          }
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to job
        </Button>
      </div>
    );
  }

  const techs =
    appointment.technicians.length > 0
      ? appointment.technicians
      : appointment.primaryTechnician
        ? [{ ...appointment.primaryTechnician, role: 'LEAD' as const }]
        : [];

  // Expenses have their own editor (not the advanced visit editor); the form defaults
  // its appointment to this visit.
  const goNewExpense = () => setNewExpenseOpen(true);

  const timeEntryCount = techs.length;
  const activeTab: VisitDetailTab = visitTab ?? 'work';
  const jobReadOnly = job
    ? READ_ONLY_JOB_STATUSES.has(String(job.statusPhase ?? job.status).toUpperCase())
    : true;

  function openJobHistory() {
    void navigate({ search: (previous) => ({ ...previous, visitTab: 'history' }) });
  }

  return (
    <div className="page-enter flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="w-full space-y-4 px-4 py-4 sm:px-5">
          <VisitCommandBar
            appointment={appointment}
            job={job}
            contact={contact}
            onBack={() =>
              void router.navigate({
                to: '/dashboard/jobs/$id',
                params: { id },
                search: { ...jobsListSearch, tab: 'overview' },
              })
            }
            nextStep={nextStep}
            busy={menuBusy}
            jobWorkspaceMenu={
              job ? (
                <JobWorkspaceMenu
                  job={job}
                  appointmentId={appointmentId}
                  pricingHidden={pricingHidden}
                  onCreateInvoice={() => setNewInvoiceOpen(true)}
                  onOpenHistory={openJobHistory}
                />
              ) : null
            }
            primaryCta={<HeaderPrimaryCta nextStep={nextStep} busy={menuBusy} />}
            overflowMenu={
              <AppointmentActionMenu
                jobId={id}
                appointment={appointment}
                isRecurring={job?.bookingType === 'RECURRING'}
                onReschedule={() => setRescheduleOpen(true)}
                onReassign={() => {
                  setPrefilledCrew(null);
                  setReassignOpen(true);
                }}
                jobUnavailableHint={jobUnavailableHint}
                skip={skipMutation}
                cancel={cancelMutation}
                busy={nextStep.isPending}
              />
            }
          />
          <VisitDecisionDialog nextStep={nextStep} busy={menuBusy} />

          <VisitOverviewStrip appointment={appointment} job={job} />

          {appointment.status === 'CANCELLED' && !jobReadOnly && (
            <RestoreVisitButton jobId={id} visitId={appointmentId} />
          )}

          {blockingPending > 0 && (
            <div className="flex items-start gap-2.5 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold">
                  {blockingTotal - blockingPending} of {blockingTotal} required checklists complete
                </p>
                <p className="text-xs">
                  Complete {blockingPending === 1 ? 'it' : 'them'} before closing the visit.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0">
              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  void navigate({
                    search: (previous) => ({
                      ...previous,
                      visitTab: value as VisitDetailTab,
                    }),
                  })
                }
                className="gap-3"
              >
                <TabsList className="w-full">
                  <TabsTrigger value="work">
                    <ClipboardCheck />
                    Work
                    {blockingPending > 0 ? (
                      <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger value="billing">
                    <CreditCard />
                    Billing
                  </TabsTrigger>
                  <TabsTrigger value="notes">
                    <StickyNote />
                    Notes &amp; files
                  </TabsTrigger>
                  <TabsTrigger value="comms">
                    <Bell />
                    Comms
                  </TabsTrigger>
                  <TabsTrigger value="history">
                    <History />
                    Job history
                  </TabsTrigger>
                </TabsList>

                {/* ── Work ── */}
                <TabsContent value="work" className="space-y-3">
                  {/* Multi-day series (Scheduling v2 §5) — reschedule/reassign/cancel the
                      REMAINING days; only rendered when the visit belongs to a series. */}
                  {appointment.seriesId ? (
                    <SeriesActionsCard jobId={id} appointment={appointment} />
                  ) : null}

                  {visitReview ? (
                    <SectionCard
                      icon={Star}
                      title="Customer review"
                      sub={
                        visitReview.internalRating != null
                          ? `${visitReview.internalRating} of 5`
                          : 'Awaiting response'
                      }
                      action={
                        <VisitReviewControls
                          jobId={id}
                          appointment={appointment}
                          review={visitReview}
                          isRecurring={isRecurring}
                          readOnly
                        />
                      }
                    >
                      {visitReview.internalRating != null ? (
                        <div className="space-y-3 border-t pt-4">
                          <div
                            className="flex items-center gap-1"
                            aria-label={`${visitReview.internalRating} out of 5 stars`}
                          >
                            {Array.from({ length: 5 }, (_, index) => (
                              <Star
                                key={index}
                                className={cn(
                                  'size-4',
                                  index < visitReview.internalRating!
                                    ? 'fill-amber-300 text-amber-400'
                                    : 'text-muted-foreground/30',
                                )}
                              />
                            ))}
                          </div>
                          <p className="text-sm leading-relaxed text-foreground">
                            {visitReview.internalFeedback?.trim() ||
                              'The customer submitted a rating without a written comment.'}
                          </p>
                        </div>
                      ) : (
                        <p className="border-t pt-4 text-sm text-muted-foreground">
                          The review request was sent and is awaiting the customer&apos;s response.
                        </p>
                      )}
                    </SectionCard>
                  ) : null}

                  {/* Inline-editable visit details — replaces the removed "Edit visit"
                      wizard. Service type stays fixed (owned by the job); only the
                      per-visit fields that used to live in the wizard are editable here. */}
                  <SectionCard
                    icon={Clock}
                    title="Schedule"
                    action={
                      !isVisitLocked && !jobReadOnly ? (
                        <Button variant="outline" size="sm" onClick={() => setReassignOpen(true)}>
                          Schedule visit
                        </Button>
                      ) : null
                    }
                  >
                    <div className="space-y-3">
                      {appointment.arrivalWindow?.displayStart ? (
                        <ArrivalWindowSection jobId={id} appointment={appointment} bare />
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Choose a date and arrival window to schedule this visit.
                        </p>
                      )}
                      <VisitScheduleSection appointment={appointment} />
                      {appointment.arrivalWindow?.displayStart && (
                        <div className="flex flex-wrap items-center gap-2">
                          {!isVisitLocked && !jobReadOnly && (
                            <SendVisitConfirmationButton appointment={appointment} />
                          )}
                          <VisitCustomerConfirmationActions
                            jobId={id}
                            appointment={appointment}
                            readOnly={isVisitLocked || jobReadOnly}
                          />
                        </div>
                      )}
                    </div>
                  </SectionCard>

                  <SectionCard icon={FileText} title="Visit details">
                    <div className="divide-y divide-border">
                      <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
                        <p className="pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:w-32 sm:shrink-0">
                          Issue summary
                        </p>
                        <div className="min-w-0 flex-1">
                          <IssueSummarySection
                            jobId={id}
                            appointment={appointment}
                            readOnly={isVisitLocked}
                            bare
                            hideDescription
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4">
                        <p className="pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:w-32 sm:shrink-0">
                          Arrival window
                        </p>
                        <div className="min-w-0 flex-1">
                          <ArrivalWindowSection
                            jobId={id}
                            appointment={appointment}
                            readOnly={isVisitLocked}
                            bare
                          />
                        </div>
                      </div>
                    </div>
                  </SectionCard>

                  {/* Full auth/sign-off card in both states: collect/configure while active,
                      read-only once locked — so completed records keep their evidence. */}
                  <SectionCard icon={ClipboardCheck} title="Completion" sub="start & finish gates">
                    <AppointmentAuthSignoffCard
                      appointment={appointment}
                      framed={false}
                      readOnly={isVisitLocked}
                      collectAuthorizationOpen={collectAuthorizationOpen}
                      onCollectAuthorizationOpenChange={(open) =>
                        setAuthorizationDialogOpen(
                          open,
                          startAfterAuthorization,
                          setCollectAuthorizationOpen,
                        )
                      }
                      onAuthorizationCollected={() =>
                        continueStartAfterAuthorization(startAfterAuthorization, () =>
                          nextStep.runAction({ kind: 'transition', status: 'IN_PROGRESS' }),
                        )
                      }
                    />
                  </SectionCard>

                  {/* Always mount AppointmentChecklists (it owns AddChecklistDialog + its own
                      empty state); gating it behind a count would hide the first-add flow. */}
                  <SectionCard icon={ListChecks} title="Checklists">
                    <AppointmentChecklists
                      appointmentId={appointmentId}
                      locked={isVisitLocked}
                      lockedReason={lockedReasonFor(appointment.status)}
                      showHeader={false}
                      jobTypeId={job?.jobType?.id ?? undefined}
                      state={checklistOffline}
                    />
                  </SectionCard>

                  {/* Always mount AppointmentFormSubmissions: it hosts RequiredFormsBanner,
                      which surfaces required forms that block completion. */}
                  <SectionCard icon={FileText} title="Forms">
                    <AppointmentFormSubmissions
                      appointmentId={appointmentId}
                      readOnly={isVisitLocked}
                    />
                  </SectionCard>
                </TabsContent>

                {/* ── Billing ── */}
                <TabsContent value="billing" className="space-y-3">
                  {job && canViewInvoices && !pricingHidden ? (
                    <SectionCard icon={Receipt} title="Job invoices" sub="shared by every visit">
                      <JobInvoicesSection
                        job={job}
                        readOnly={jobReadOnly || !canCreateInvoices}
                        onCreateInvoice={
                          canCreateInvoices && !jobReadOnly
                            ? () => setNewInvoiceOpen(true)
                            : undefined
                        }
                      />
                    </SectionCard>
                  ) : null}
                  <ChargesCard appointment={appointment} job={job} locked={isVisitLocked} />
                  <ExpensesCard
                    expenses={visitExpenses}
                    locked={isVisitLocked}
                    onAdd={goNewExpense}
                    onSelect={setSelectedExpenseId}
                  />
                  <SectionCard
                    icon={Clock}
                    title="Time entries"
                    sub={
                      timeEntryCount > 0
                        ? `${timeEntryCount} ${timeEntryCount === 1 ? 'technician' : 'technicians'}`
                        : undefined
                    }
                  >
                    {timeEntryCount === 0 ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3.5 py-3 text-sm text-muted-foreground">
                        No time logged. Hours the technician clocks against this visit roll up here.
                      </div>
                    ) : (
                      <AppointmentTimeEntries
                        appointmentId={appointmentId}
                        jobId={id}
                        technicians={techs}
                        readOnly
                      />
                    )}
                  </SectionCard>
                </TabsContent>

                {/* ── Notes & files ── */}
                <TabsContent value="notes" className="space-y-3">
                  <SectionCard icon={StickyNote} title="Notes">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="overflow-hidden rounded-lg border">
                        <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold">
                          <span className="inline-flex items-center gap-1.5">
                            <MessageSquare className="size-3.5" />
                            Customer notes
                          </span>
                          <span className="font-medium text-brand-light-grey">from booking</span>
                        </div>
                        <CustomerNotesSection
                          jobId={id}
                          appointment={appointment}
                          bare
                          hideDescription
                        />
                      </div>
                      <div className="overflow-hidden rounded-lg border">
                        <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-semibold">
                          <span className="inline-flex items-center gap-1.5">
                            <Lock className="size-3.5" />
                            Internal notes
                          </span>
                          <span className="font-medium text-brand-light-grey">team only</span>
                        </div>
                        <InternalNotesSection
                          jobId={id}
                          appointment={appointment}
                          bare
                          hideDescription
                        />
                      </div>
                    </div>
                  </SectionCard>

                  {/* One timeline: every visit note, each with its own photos
                      and files. Files are never a separate list. */}
                  <SectionCard
                    icon={Paperclip}
                    title="Notes & files"
                    action={
                      isVisitLocked ? null : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setAddNoteSignal((value) => value + 1)}
                        >
                          <Plus className="size-4" />
                          Add note
                        </Button>
                      )
                    }
                  >
                    <EntityNotes
                      entityType="APPOINTMENT"
                      entityId={appointmentId}
                      hideHeader
                      createSignal={addNoteSignal}
                      emptyText="No notes on this visit yet. Add one to capture what happened — with photos or files if you have them."
                      readOnly={isVisitLocked}
                    />
                    {appointment.attachments?.length ? (
                      <div className="space-y-2 border-t p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Standalone files
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Legacy files uploaded before Visit files were stored on notes.
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {appointment.attachments.map((file) => (
                            <a
                              key={file.id}
                              href={file.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex min-w-0 items-center gap-2 rounded-md border p-3 text-sm hover:bg-muted/50"
                            >
                              <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                              <span className="min-w-0 flex-1 truncate">{file.fileName}</span>
                              {file.fileSize ? (
                                <span className="shrink-0 text-xs text-muted-foreground">
                                  {Math.max(1, Math.round(file.fileSize / 1024))} KB
                                </span>
                              ) : null}
                            </a>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </SectionCard>
                </TabsContent>

                {/* ── Comms ── */}
                <TabsContent value="comms" className="space-y-3">
                  <SectionCard icon={Bell} title="Confirmation & reminders">
                    {confirmationReminder ||
                    jobReminderReminder ||
                    appointment.confirmationStatus === 'CONFIRMED' ? (
                      <div className="space-y-4">
                        <ConfirmationSection jobId={id} appointment={appointment} bare />
                        <VisitRemindersSection jobId={id} appointment={appointment} bare />
                      </div>
                    ) : (
                      <p className="rounded-lg border border-dashed border-border bg-muted/30 px-3.5 py-3 text-sm text-muted-foreground">
                        {appointmentReminders.canView
                          ? 'No messages yet. No confirmation or reminder cadence is running for this visit — sent and upcoming messages appear here once one is.'
                          : 'Confirmation and reminder messages are not available with your role.'}
                      </p>
                    )}
                  </SectionCard>
                </TabsContent>

                {/* ── Job history ── */}
                <TabsContent value="history" className="space-y-3">
                  <RecurringActivityTab jobId={id} enabled={activeTab === 'history'} />
                  {job ? <JobRelationshipsCard jobId={id} pricingHidden={pricingHidden} /> : null}
                </TabsContent>
              </Tabs>
            </div>

            <VisitAside
              appointment={appointment}
              job={job}
              hideBilling
              hideGlance
              hideScope
              onReassignCrew={() => {
                setPrefilledCrew(null);
                setReassignOpen(true);
              }}
              crewActionHint={jobUnavailableHint}
            />
          </div>
        </div>
      </div>

      {job && (
        <VisitSchedulingPanel
          readOnly={isVisitLocked || jobReadOnly}
          open={reassignOpen}
          onClose={() => {
            setReassignOpen(false);
            setPrefilledCrew(null);
          }}
          jobId={id}
          job={job}
          appointment={appointment}
          initialCrewOverride={prefilledCrew}
          onConflict={(nextCrew) => {
            setReassignOpen(false);
            setPrefilledCrew(nextCrew);
            setRescheduleOpen(true);
          }}
        />
      )}

      {job && (
        <RescheduleAppointmentDialog
          open={rescheduleOpen}
          onClose={() => {
            setRescheduleOpen(false);
            setPrefilledCrew(null);
          }}
          jobId={id}
          job={job}
          appointment={appointment}
          prefilledCrew={prefilledCrew}
          onBackToReassign={
            prefilledCrew && prefilledCrew.length > 0
              ? () => {
                  setRescheduleOpen(false);
                  setReassignOpen(true);
                }
              : undefined
          }
        />
      )}

      {job && (
        <NewExpenseDrawer
          jobId={id}
          appointmentId={appointmentId}
          open={newExpenseOpen}
          onOpenChange={setNewExpenseOpen}
        />
      )}

      {job && (
        <ExpenseDetailDrawer
          jobId={id}
          expenseId={selectedExpenseId}
          open={Boolean(selectedExpenseId)}
          onOpenChange={(open) => {
            if (!open) setSelectedExpenseId(null);
          }}
          readOnly={isVisitLocked}
        />
      )}

      {job && canCreateInvoices && !pricingHidden && (
        <NewInvoiceDrawer jobId={id} open={newInvoiceOpen} onOpenChange={setNewInvoiceOpen} />
      )}
    </div>
  );
}
